// Ticket 25 (107-coverage-navigator-gaps) — delivery-mode chips for the
// navigator card meta row. Variant A from ticket 16's prototype (approved
// 03/09/2026): one labelled icon chip per mode the specialty offers, tooltip
// explains the mode to the care seeker, trigger is a real <button> so it
// opens on focus (Radix Tooltip) and works on tap, not just hover.
import type { ReactNode } from 'react';
import { Building2, Home, Video } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { DELIVERY_MODE_HINT, DELIVERY_MODE_LABEL } from './deliveryMode';
import type { DeliveryMode } from './navigator-results.types';

const MODE_ICON = { IN_HOME: Home, VIRTUAL: Video, IN_OFFICE: Building2 } as const;

type DeliveryModeChipsProps = {
  modes?: DeliveryMode[];
  /** Alternate rows and the carousel row — same treatment, less room. */
  compact?: boolean;
};

const ModeTooltip = ({ mode, children }: { mode: DeliveryMode; children: ReactNode }) => (
  <TooltipProvider delayDuration={120}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${DELIVERY_MODE_LABEL[mode]}: ${DELIVERY_MODE_HINT[mode]}`}
          className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        // Warm sage panel — matches NavigatorReasonHint, not the default
        // gray-900 tooltip chrome (108 ticket 03).
        className="max-w-[15rem] rounded-xl border border-sage/20 bg-card-base px-3.5 py-2.5 text-text-primary shadow-[0_8px_24px_rgba(15,23,42,0.08)]"
      >
        <p className="mb-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-sage">
          {DELIVERY_MODE_LABEL[mode]}
        </p>
        <p className="text-xs leading-relaxed text-text-primary">{DELIVERY_MODE_HINT[mode]}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/**
 * One chip per delivery mode the searched specialty offers. Renders nothing
 * without data — production (today) or a specialty that declares none never
 * draws an "unknown" chip (ticket 16, sub-question f).
 */
export const DeliveryModeChips = ({ modes, compact = false }: DeliveryModeChipsProps) => {
  if (!modes || modes.length === 0) return null;

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {modes.map((mode) => {
        const Icon = MODE_ICON[mode];
        return (
          <ModeTooltip key={mode} mode={mode}>
            <span
              className={`inline-flex items-center gap-1 rounded-full border border-sage/25 bg-sage-bg text-sage ${
                compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'
              }`}
            >
              <Icon className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden />
              {DELIVERY_MODE_LABEL[mode]}
            </span>
          </ModeTooltip>
        );
      })}
    </span>
  );
};
